import React from 'react';

export function Section({
    icon,
    title,
    subtitle,
    isOpen,
    onEdit,
    children,
}: {
    icon: React.ReactNode;
    title: string;
    subtitle: string;
    isOpen: boolean;
    onEdit: () => void;
    children: React.ReactNode;
}) {
    return (
        <div className="rounded-[28px] bg-white ring-1 ring-black/[0.06] dark:bg-white/[0.04] dark:ring-white/10 overflow-hidden">
            <div className="w-full flex items-center justify-between gap-4 px-5 sm:px-7 py-5">
                <div className="flex items-center gap-3 min-w-0">
                    <span className="w-11 h-11 rounded-2xl bg-primary/10 dark:bg-nightAccent/15 text-primary dark:text-nightAccent flex items-center justify-center flex-shrink-0">{icon}</span>
                    <div className="min-w-0">
                        <div className="text-lg font-bold tracking-[-0.02em] text-grey10 dark:text-white">{title}</div>
                        <div className="text-sm text-grey40 dark:text-nightMuted truncate">{subtitle}</div>
                    </div>
                </div>
                {!isOpen && (
                    <button
                        type="button"
                        onClick={onEdit}
                        className="inline-flex h-10 items-center rounded-full px-5 text-sm font-semibold text-grey10 ring-1 ring-grey90 transition-colors hover:bg-grey99 dark:text-white dark:ring-white/15 dark:hover:bg-white/[0.06] flex-shrink-0"
                    >
                        Edit
                    </button>
                )}
            </div>
            {isOpen && (
                <div className="px-5 sm:px-7 pb-7 pt-6 border-t border-grey95 dark:border-white/10 flex flex-col gap-6">
                    {children}
                </div>
            )}
        </div>
    );
}

// Always-expanded section: header (icon/title/subtitle) + body, no collapse/Edit toggle.
// Matches the "Info" tab layout used when editing an event (EventComponent.tsx's InfoSection).
export function FlatSection({
    icon,
    title,
    subtitle,
    children,
}: {
    icon: React.ReactNode;
    title: string;
    subtitle?: string;
    children: React.ReactNode;
}) {
    return (
        <div className="rounded-[28px] bg-white ring-1 ring-black/[0.06] dark:bg-white/[0.04] dark:ring-white/10 overflow-hidden">
            <div className="flex items-center gap-3 px-5 sm:px-7 py-5 border-b border-grey95 dark:border-white/10">
                <span className="w-11 h-11 rounded-2xl bg-primary/10 dark:bg-nightAccent/15 text-primary dark:text-nightAccent flex items-center justify-center flex-shrink-0">{icon}</span>
                <div className="min-w-0">
                    <div className="text-lg font-bold tracking-[-0.02em] text-grey10 dark:text-white">{title}</div>
                    {subtitle && <div className="text-sm text-grey40 dark:text-nightMuted truncate">{subtitle}</div>}
                </div>
            </div>
            <div className="px-5 sm:px-7 py-7 flex flex-col gap-6">
                {children}
            </div>
        </div>
    );
}

export const AboutSectionIcon = () => (
    <svg width={18} height={18} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx={12} cy={12} r={9} stroke="currentColor" strokeWidth={1.5} />
        <path d="M12 11v5" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" />
        <circle cx={12} cy={8} r={1} fill="currentColor" />
    </svg>
);
export const VisualsSectionIcon = () => (
    <svg width={18} height={18} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x={3} y={4} width={18} height={14} rx={2} stroke="currentColor" strokeWidth={1.5} />
        <circle cx={8} cy={9} r={1.5} stroke="currentColor" strokeWidth={1.5} />
        <path d="M3 15l5-4 4 3 3-2 6 5" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);
export const LinksSectionIcon = () => (
    <svg width={18} height={18} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M9 12a3.5 3.5 0 0 1 0-5l2-2a3.5 3.5 0 0 1 5 5l-1 1" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
        <path d="M15 12a3.5 3.5 0 0 1 0 5l-2 2a3.5 3.5 0 0 1-5-5l1-1" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);
export const AccessSectionIcon = () => (
    <svg width={18} height={18} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 3l7 3v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3Z" stroke="currentColor" strokeWidth={1.5} strokeLinejoin="round" />
    </svg>
);
export const EmbedSectionIcon = () => (
    <svg width={18} height={18} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M8 9l-4 3 4 3M16 9l4 3-4 3M14 6l-4 12" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);
